/**
 * Marks history entries whose scroll position must not be restored.
 *
 * A page left through the next-listing turn was left at the scroll clamp,
 * looking at the next page's own opening grown to full size. Restoring that
 * position on Back shows the reader a full-size picture of the page they
 * just came back from — Back should read as "back to that page", which is
 * its top.
 *
 * The turn marks the entry it departs; `restorationKey` then answers with a
 * different key for marked entries, so the first return finds no saved
 * position and ScrollRestoration falls back to the top. The entry keeps the
 * marked key from there on, so everything after that first return saves and
 * restores as normal.
 */

const PREFIX = 'restore-at-top:';

export const markRestoreAtTop = (locationKey: string) => {
    try {
        sessionStorage.setItem(PREFIX + locationKey, '1');
    } catch {
        // Storage full or blocked: Back restores the clamp, which is the
        // behaviour this improves on, not something to break navigation for.
    }
};

const PENDING = 'restore-at-top:pending';

/**
 * Marks an entry once the navigation leaving it has been recorded.
 *
 * The mark cannot be set while still on the page: the router saves the
 * departing scroll position under `restorationKey(location)`, so marking
 * first would file the clamp under the marked key and Back would restore it
 * after all. The scroll-driven turn sidesteps this by marking from the
 * landing, which runs after the save; a link has no landing of its own, so
 * it leaves the key here and the next location picks it up.
 */
export const markRestoreAtTopAfterLanding = (locationKey: string) => {
    try {
        sessionStorage.setItem(PENDING, locationKey);
    } catch {
        // Same trade as markRestoreAtTop: Back restores the clamp.
    }
};

/** Applies any deferred mark. Safe to call on every location change. */
export const consumePendingRestoreAtTop = (currentKey: string) => {
    try {
        const pending = sessionStorage.getItem(PENDING);
        if (!pending) return;
        // Cleared whether or not it is applied here. Left behind, a mark that
        // did not apply is picked up by the next navigation instead, and
        // sends an entry nobody asked to leave back to its top.
        sessionStorage.removeItem(PENDING);
        if (pending === currentKey) return;
        markRestoreAtTop(pending);
    } catch {
        // As above.
    }
};

export const restorationKey = (location: { key: string }): string => {
    try {
        return sessionStorage.getItem(PREFIX + location.key)
            ? `${location.key}:top`
            : location.key;
    } catch {
        return location.key;
    }
};
